import React, { useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Upload, FileText, Loader2, ArrowLeft, Sparkles, Save,
  Plus, Trash2, Users, CalendarDays, Trophy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors backend/sport_definitions.py — kept in-file so the chip row can
// render instantly (no extra fetch) when the user changes sport.
// The first stat in each list is the "primary" one (e.g. Goals for soccer)
// and gets a filled pill so users see the scoring stat at a glance.
const SPORT_TRACKED_STATS = {
  basketball: ['Points', 'Rebounds', 'Assists', 'Steals', 'Blocks', 'Turnovers'],
  soccer: ['Goals', 'Assists', 'Shots', 'Saves', 'Yellow Cards', 'Red Cards'],
  volleyball: ['Kills', 'Aces', 'Blocks', 'Digs', 'Assists', 'Serve Errors'],
  netball: ['Goals', 'Assists', 'Intercepts', 'Rebounds', 'Turnovers'],
  pickleball: ['Winners', 'Aces', 'Dinks', 'Volleys', 'Errors'],
  flag_football: ['Touchdowns', 'Extra Points', 'Catches', 'Flag Pulls', 'Interceptions', 'Sacks'],
  softball: ['Hits', 'At-Bats', 'Runs', 'RBIs', 'Home Runs', 'Walks', 'Strikeouts'],
  baseball: ['Hits', 'At-Bats', 'Runs', 'RBIs', 'Home Runs', 'Walks', 'Strikeouts'],
  cricket: ['Runs', 'Balls Faced', 'Fours', 'Sixes', 'Wickets', 'Runs Conceded', 'Catches'],
  generic: [],
};

export default function ImportSchedulePage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const fileInputRef = useRef(null);

  const [phase, setPhase] = useState('upload'); // 'upload' | 'preview'
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState({ name: '', start_date: '', venue: '', sport: 'basketball' });

  React.useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [authLoading, user, navigate]);

  const handleFilePick = (f) => {
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    const isPdf = f.type === 'application/pdf' || name.endsWith('.pdf');
    const isCsv = f.type === 'text/csv' || name.endsWith('.csv') || name.endsWith('.tsv');
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls') ||
      (f.type || '').includes('spreadsheetml') ||
      (f.type || '').includes('ms-excel');
    if (!isPdf && !isCsv && !isXlsx) {
      toast.error('Please upload a PDF, CSV, or Excel (.xlsx) file');
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error('File is too large (max 15 MB)');
      return;
    }
    setFile(f);
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const token = localStorage.getItem('session_token');
      const form = new FormData();
      form.append('file', file);
      const name = (file.name || '').toLowerCase();
      const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
      const endpoint = isPdf ? 'parse-pdf' : 'parse-spreadsheet';
      const res = await fetch(`${API_URL}/api/events/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Extraction failed');
      const p = data.preview;
      setPreview(p);
      setMeta({
        name: p.event_name || file.name.replace(/\.(pdf|csv|tsv|xlsx|xls)$/i, ''),
        start_date: p.start_date || new Date().toISOString().slice(0, 10),
        venue: p.venue || '',
        sport: p.sport || 'basketball',
      });
      setPhase('preview');
      toast.success(`Extracted ${p.teams.length} teams and ${p.matches.length} games`);
    } catch (e) {
      toast.error(e.message || 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  // ---------- Preview editing helpers ----------
  const updateTeam = (idx, value) => {
    setPreview((p) => {
      const oldName = p.teams[idx];
      const newTeams = [...p.teams];
      newTeams[idx] = value;
      // Cascade rename in matches
      const newMatches = p.matches.map((m) => ({
        ...m,
        team_a: m.team_a === oldName ? value : m.team_a,
        team_b: m.team_b === oldName ? value : m.team_b,
      }));
      return { ...p, teams: newTeams, matches: newMatches };
    });
  };

  const removeTeam = (idx) => {
    setPreview((p) => {
      const removed = p.teams[idx];
      const newTeams = p.teams.filter((_, i) => i !== idx);
      const newMatches = p.matches.filter((m) => m.team_a !== removed && m.team_b !== removed);
      return { ...p, teams: newTeams, matches: newMatches };
    });
  };

  const addTeam = () => {
    setPreview((p) => ({ ...p, teams: [...p.teams, `Team ${p.teams.length + 1}`] }));
  };

  const updateMatch = (idx, field, value) => {
    setPreview((p) => {
      const next = [...p.matches];
      next[idx] = { ...next[idx], [field]: value };
      return { ...p, matches: next };
    });
  };

  const removeMatch = (idx) => {
    setPreview((p) => ({ ...p, matches: p.matches.filter((_, i) => i !== idx) }));
  };

  const addMatch = () => {
    setPreview((p) => ({
      ...p,
      matches: [
        ...p.matches,
        { date: meta.start_date, time: '10:00', court: '', venue: '', team_a: p.teams[0] || '', team_b: p.teams[1] || '' },
      ],
    }));
  };

  const isValidPreview = useMemo(() => {
    if (!preview) return false;
    if (!meta.name.trim() || !meta.start_date) return false;
    if (preview.teams.length < 2) return false;
    if (preview.matches.length < 1) return false;
    return preview.matches.every((m) => m.team_a && m.team_b && m.team_a !== m.team_b);
  }, [preview, meta]);

  const handleImport = async () => {
    if (!isValidPreview) {
      toast.error('Fill in event name, start date, at least 2 teams and 1 match');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        name: meta.name.trim(),
        start_date: meta.start_date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        venue: meta.venue || null,
        sport: meta.sport || 'basketball',
        teams: preview.teams,
        matches: preview.matches.map((m) => ({
          date: m.date || null,
          time: m.time || null,
          court: m.court || null,
          venue: m.venue || null,
          team_a: m.team_a,
          team_b: m.team_b,
        })),
      };
      const res = await fetch(`${API_URL}/api/events/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      toast.success('Schedule imported successfully');
      navigate(`/schedule/${data.event.id}`);
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/my-schedules" className="flex items-center gap-2 text-slate-600 hover:text-[#00247D]" data-testid="import-back-link">
            <ArrowLeft className="w-4 h-4" /> My Schedules
          </Link>
          <img src="/logo-transparent.png" alt="Schedule Guru" className="h-10" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-[#00247D] mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">AI-Powered Import</span>
          </div>
          <h1 className="text-3xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
            IMPORT SCHEDULE
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Upload an existing schedule (PDF, Excel, or CSV) and we&apos;ll extract the teams, dates, times,
            and matchups automatically. You&apos;ll get a chance to review and edit before it&apos;s saved.
          </p>
        </div>

        {phase === 'upload' && (
          <Card className="border-slate-200 max-w-2xl">
            <CardContent className="py-10">
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                  file ? 'border-[#00247D] bg-blue-50/40' : 'border-slate-300 hover:border-[#00247D]/60'
                }`}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handleFilePick(e.dataTransfer.files?.[0]); }}
                data-testid="pdf-dropzone"
              >
                {file ? (
                  <div className="space-y-3">
                    <FileText className="w-12 h-12 text-[#00247D] mx-auto" />
                    <p className="font-medium text-slate-800" data-testid="selected-pdf-name">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</p>
                    <div className="flex justify-center gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => { setFile(null); fileInputRef.current && (fileInputRef.current.value = ''); }}
                        data-testid="clear-pdf-btn"
                      >
                        Choose Different File
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-slate-400 mx-auto" />
                    <p className="text-slate-700 font-medium">Drop your schedule here</p>
                    <p className="text-xs text-slate-500">PDF, Excel (.xlsx), or CSV</p>
                    <p className="text-xs text-slate-500">or</p>
                    <Button onClick={() => fileInputRef.current?.click()} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="pdf-pick-btn">
                      Choose File
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf,.pdf,text/csv,.csv,.tsv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      className="hidden"
                      onChange={(e) => handleFilePick(e.target.files?.[0])}
                      data-testid="pdf-file-input"
                    />
                    <p className="text-xs text-slate-400 pt-2">Max 15 MB • processed in-memory</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleParse}
                  disabled={!file || parsing}
                  className="bg-[#00247D] hover:bg-[#001A5C]"
                  data-testid="extract-btn"
                >
                  {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {parsing ? 'Extracting…' : 'Extract Schedule'}
                </Button>
              </div>

              <div className="mt-6 text-xs text-slate-500 bg-slate-50 rounded-md p-3 border border-slate-100">
                <span className="font-medium text-slate-600">Tip:</span> Cleaner files (typed text, one game per row) extract more accurately. Spreadsheets with labeled columns (Date, Time, Home Team, Away Team) work best.
              </div>
            </CardContent>
          </Card>
        )}

        {phase === 'preview' && preview && (
          <div className="space-y-6">
            {/* Event meta */}
            <Card className="border-slate-200">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-[#00247D]" /> Event Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label>Event Name *</Label>
                    <Input
                      value={meta.name}
                      onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                      data-testid="import-event-name"
                    />
                  </div>
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={meta.start_date}
                      onChange={(e) => setMeta({ ...meta, start_date: e.target.value })}
                      data-testid="import-event-start"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5">
                      <Trophy className="w-3.5 h-3.5 text-amber-500" />
                      Sport *
                    </Label>
                    <Select
                      value={meta.sport}
                      onValueChange={(v) => setMeta({ ...meta, sport: v })}
                    >
                      <SelectTrigger data-testid="import-event-sport">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basketball" data-testid="import-sport-basketball">Basketball</SelectItem>
                        <SelectItem value="soccer" data-testid="import-sport-soccer">Soccer</SelectItem>
                        <SelectItem value="volleyball" data-testid="import-sport-volleyball">Volleyball</SelectItem>
                        <SelectItem value="netball" data-testid="import-sport-netball">Netball</SelectItem>
                        <SelectItem value="pickleball" data-testid="import-sport-pickleball">Pickleball</SelectItem>
                        <SelectItem value="flag_football" data-testid="import-sport-flag_football">Flag Football</SelectItem>
                        <SelectItem value="softball" data-testid="import-sport-softball">Softball</SelectItem>
                        <SelectItem value="baseball" data-testid="import-sport-baseball">Baseball</SelectItem>
                        <SelectItem value="cricket" data-testid="import-sport-cricket">Cricket</SelectItem>
                        <SelectItem value="generic" data-testid="import-sport-generic">Generic (Score Only)</SelectItem>
                      </SelectContent>
                    </Select>
                    {(() => {
                      const stats = SPORT_TRACKED_STATS[meta.sport] || [];
                      if (stats.length === 0) {
                        return (
                          <div
                            className="mt-2 flex items-center gap-1.5 flex-wrap"
                            data-testid="import-sport-chips"
                          >
                            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                              This will track:
                            </span>
                            <span
                              className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                              data-testid="import-sport-chip-score-only"
                            >
                              Final score only
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div
                          className="mt-2 flex items-center gap-1.5 flex-wrap"
                          data-testid="import-sport-chips"
                        >
                          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                            This will track:
                          </span>
                          {stats.map((label, idx) => (
                            <span
                              key={label}
                              className={
                                idx === 0
                                  ? 'text-[11px] px-2 py-0.5 rounded-full bg-[#00247D] text-white font-medium'
                                  : 'text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-[#00247D] border border-blue-100'
                              }
                              data-testid={`import-sport-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <Label>Venue</Label>
                    <Input
                      value={meta.venue}
                      onChange={(e) => setMeta({ ...meta, venue: e.target.value })}
                      placeholder="Optional"
                      data-testid="import-event-venue"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Teams */}
            <Card className="border-slate-200">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#00247D]" /> Teams ({preview.teams.length})
                  </h2>
                  <Button variant="outline" size="sm" onClick={addTeam} data-testid="import-add-team">
                    <Plus className="w-4 h-4 mr-1" /> Add Team
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {preview.teams.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={t}
                        onChange={(e) => updateTeam(i, e.target.value)}
                        data-testid={`import-team-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeTeam(i)}
                        className="text-slate-400 hover:text-red-500 p-1"
                        title="Remove team"
                        data-testid={`import-remove-team-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Matches */}
            <Card className="border-slate-200">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-[#00247D]" /> Games ({preview.matches.length})
                  </h2>
                  <Button variant="outline" size="sm" onClick={addMatch} data-testid="import-add-match">
                    <Plus className="w-4 h-4 mr-1" /> Add Game
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                        <th className="py-2 pr-2 font-medium">#</th>
                        <th className="py-2 pr-2 font-medium">Date</th>
                        <th className="py-2 pr-2 font-medium">Time</th>
                        <th className="py-2 pr-2 font-medium">Team A</th>
                        <th className="py-2 pr-2 font-medium">Team B</th>
                        <th className="py-2 pr-2 font-medium">Court</th>
                        <th className="py-2 pr-2 font-medium">Venue</th>
                        <th className="py-2 pr-0 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.matches.map((m, i) => (
                        <tr key={i} className="border-b border-slate-100 align-top">
                          <td className="py-2 pr-2 text-slate-500">{i + 1}</td>
                          <td className="py-2 pr-2">
                            <Input
                              type="date"
                              value={m.date || ''}
                              onChange={(e) => updateMatch(i, 'date', e.target.value)}
                              className="h-8 min-w-[130px]"
                              data-testid={`import-match-${i}-date`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              type="time"
                              value={m.time || ''}
                              onChange={(e) => updateMatch(i, 'time', e.target.value)}
                              className="h-8 min-w-[95px]"
                              data-testid={`import-match-${i}-time`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <select
                              value={m.team_a || ''}
                              onChange={(e) => updateMatch(i, 'team_a', e.target.value)}
                              className="h-8 text-sm border border-slate-200 rounded px-2 min-w-[130px] w-full bg-white"
                              data-testid={`import-match-${i}-team-a`}
                            >
                              <option value="">—</option>
                              {preview.teams.map((t) => (<option key={t} value={t}>{t}</option>))}
                            </select>
                          </td>
                          <td className="py-2 pr-2">
                            <select
                              value={m.team_b || ''}
                              onChange={(e) => updateMatch(i, 'team_b', e.target.value)}
                              className="h-8 text-sm border border-slate-200 rounded px-2 min-w-[130px] w-full bg-white"
                              data-testid={`import-match-${i}-team-b`}
                            >
                              <option value="">—</option>
                              {preview.teams.map((t) => (<option key={t} value={t}>{t}</option>))}
                            </select>
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              value={m.court || ''}
                              onChange={(e) => updateMatch(i, 'court', e.target.value)}
                              className="h-8 w-16"
                              data-testid={`import-match-${i}-court`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              value={m.venue || ''}
                              onChange={(e) => updateMatch(i, 'venue', e.target.value)}
                              className="h-8 min-w-[110px]"
                              data-testid={`import-match-${i}-venue`}
                            />
                          </td>
                          <td className="py-2 pr-0 text-right">
                            <button
                              type="button"
                              onClick={() => removeMatch(i)}
                              className="text-slate-400 hover:text-red-500 p-1"
                              title="Remove game"
                              data-testid={`import-remove-match-${i}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setPhase('upload')} disabled={saving} data-testid="import-back-to-upload">
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={!isValidPreview || saving}
                className="bg-[#00247D] hover:bg-[#001A5C]"
                data-testid="import-confirm-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Saving…' : 'Import Schedule'}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
